import { useInfoTickData } from 'src/hooks/v3/usePoolTickData';
import { useMintState } from 'src/state/mintStore';
import { Presets } from 'src/types/presets';
import { FiZoomIn, FiZoomOut } from 'react-icons/fi';
import {
  CurrencyAmount,
  INITIAL_POOL_FEE,
  Pool,
  Token,
  TickMath,
  Price,
  Currency
} from '@cryptoalgebra/kim-sdk';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { Chart } from './chart';
import { MAX_UINT128 } from '@app/constants/common';
import {
  Box,
  Button,
  Center,
  HStack,
  Skeleton,
  Spinner,
  Text
} from '@chakra-ui/react';
import { useAsync } from 'react-use';
import { PoolStateType } from '@app/hooks/v3/usePool';

interface LiquidityChartProps {
  currencyA: Currency | undefined;
  currencyB: Currency | undefined;
  currentPrice: number | undefined;
  priceLower: Price<Token, Token> | undefined;
  priceUpper: Price<Token, Token> | undefined;
  poolState: PoolStateType;
}

const ZOOM_STEP = 5;

const LiquidityChart = ({
  currencyA,
  currencyB,
  currentPrice,
  priceLower,
  priceUpper,
  poolState
}: LiquidityChartProps) => {
  const { preset } = useMintState();
  const [zoom, setZoom] = useState(50);

  const {
    fetchTicksSurroundingPrice: {
      ticksResult,
      ticksLoading,
      fetchTicksSurroundingPrice
    }
  } = useInfoTickData();

  useEffect(() => {
    if (!currencyA || !currencyB) return;

    fetchTicksSurroundingPrice(currencyA, currencyB);
    // eslint-disable-next-line
  }, [currencyA, currencyB]);

  const { value: processedData, loading } = useAsync(async () => {
    if (!ticksResult) return;

    if (!ticksResult || !ticksResult.ticksProcessed) return;

    return await Promise.all(
      ticksResult.ticksProcessed.map(async (t, i) => {
        const active = t.tickIdx === ticksResult.activeTickIdx;
        const sqrtPriceX96 = TickMath.getSqrtRatioAtTick(t.tickIdx);
        const mockTicks = [
          {
            index: Number(t.tickIdx) - Number(ticksResult.tickSpacing),
            liquidityGross: t.liquidityGross.toString(),
            liquidityNet: (t.liquidityNet * -1n).toString()
          },
          {
            index: t.tickIdx,
            liquidityGross: t.liquidityGross.toString(),
            liquidityNet: t.liquidityNet.toString()
          }
        ];
        const pool =
          currencyA && currencyB
            ? new Pool(
                currencyA.wrapped,
                currencyB.wrapped,
                INITIAL_POOL_FEE,
                sqrtPriceX96,
                t.liquidityActive.toString(),
                t.tickIdx,
                ticksResult.tickSpacing,
                mockTicks
              )
            : undefined;

        const nextSqrtX96 = ticksResult.ticksProcessed[i - 1]
          ? TickMath.getSqrtRatioAtTick(
              ticksResult.ticksProcessed[i - 1].tickIdx
            )
          : undefined;

        const maxAmountToken0 = currencyA
          ? CurrencyAmount.fromRawAmount(
              currencyA.wrapped,
              MAX_UINT128.toString()
            )
          : undefined;

        const outputRes0 =
          pool && maxAmountToken0
            ? await pool.getOutputAmount(maxAmountToken0, nextSqrtX96)
            : undefined;

        const token1Amount = outputRes0?.[0] as
          | CurrencyAmount<Token>
          | undefined;

        const amount0 = token1Amount
          ? parseFloat(token1Amount.toExact()) * parseFloat(t.price1)
          : 0;
        const amount1 = token1Amount ? parseFloat(token1Amount.toExact()) : 0;

        return {
          index: i,
          isCurrent: active,
          activeLiquidity: parseFloat(t.liquidityActive.toString()),
          price0: parseFloat(t.price0),
          price1: parseFloat(t.price1),
          tvlToken0: amount0,
          tvlToken1: amount1
        };
      })
    ).catch(e => {
      console.error(e);
    });
  }, [currencyA, currencyB, ticksResult]);

  useEffect(() => {
    if (preset === null) return;

    switch (preset) {
      case Presets.FULL:
        setZoom(10);
        break;
      case Presets.NORMAL:
        setZoom(25);
        break;
      case Presets.RISK:
        setZoom(30);
        break;
      case Presets.SAFE:
        setZoom(15);
        break;
      case Presets.STABLE:
        setZoom(40);
        break;
    }
  }, [preset]);

  const formattedData = useMemo(() => {
    if (!processedData) return undefined;

    if (processedData && processedData.length === 0) return undefined;

    const middle = Math.round(processedData.length / 2);
    const chunkLength = Math.round(processedData.length / zoom);

    const slicedData = processedData.slice(
      middle - chunkLength,
      middle + chunkLength
    );

    return slicedData.reverse();
  }, [processedData, zoom]);

  const isSorted =
    currencyA &&
    currencyB &&
    currencyA?.wrapped.sortsBefore(currencyB?.wrapped);

  const leftPrice = useMemo(() => {
    return isSorted
      ? priceLower?.toSignificant(18)
      : priceUpper?.invert().toSignificant(18);
  }, [isSorted, priceLower, priceUpper]);

  const rightPrice = useMemo(() => {
    return isSorted
      ? priceUpper?.toSignificant(18)
      : priceLower?.invert().toSignificant(18);
  }, [isSorted, priceLower, priceUpper]);

  const isZoomMin = zoom - ZOOM_STEP <= 10;
  const isZoomMax = zoom + ZOOM_STEP > 40;

  const handleZoomIn = () => setZoom(zoom => zoom + ZOOM_STEP);
  const handleZoomOut = () => setZoom(zoom => zoom - ZOOM_STEP);

  function renderState(label: ReactNode) {
    return (
      <Center
        bg="neutral.1000"
        color="neutral.100"
        sx={{
          width: '100%',
          height: '100%',
          flex: 1,
          minHeight: '300px'
        }}
      >
        {label}
      </Center>
    );
  }

  function renderContent() {
    // if (loading || ticksLoading) {
    //   return <LiquidityChartLoader />;
    // }

    if (!currencyA || !currencyB) {
      return renderState('Select a pair');
    }

    switch (poolState) {
      case 'LOADING': {
        return renderState(<Spinner color="orange" width={8} height={8} />);
      }
      case 'NOT_EXISTS': {
        return loading || ticksLoading
          ? renderState(<Spinner color="orange" width={8} height={8} />)
          : renderState('Pool is not initialized');
      }
      case 'INVALID': {
        return renderState('Invalid pool');
      }
      default: {
        if (formattedData && currencyA && currencyB) {
          return (
            <Chart
              formattedData={formattedData}
              leftPrice={leftPrice}
              rightPrice={rightPrice}
              currentPrice={currentPrice}
              isSorted={isSorted}
              zoom={zoom}
              currencyA={currencyA}
              currencyB={currencyB}
            />
          );
        }

        if (loading || ticksLoading) {
          return renderState(<Spinner color="orange" width={8} height={8} />);
        }

        return renderState('Cannot render visualizer');
      }
    }
  }

  return (
    <Box w="100%">
      <HStack
        bg="gray.2"
        borderRadius={4}
        w="100%"
        justifyContent="space-between"
        p="12px 16px"
      >
        <Text fontSize={16}>Range visualizer</Text>
        <HStack>
          <Button
            variant="tertiary"
            _hover={{
              color: 'orange'
            }}
            sx={{
              height: '26px'
            }}
            isDisabled={isZoomMin}
            onClick={handleZoomOut}
          >
            <FiZoomOut />
          </Button>
          <Button
            variant="tertiary"
            sx={{
              height: '26px'
            }}
            _hover={{
              color: 'orange'
            }}
            isDisabled={isZoomMax}
            onClick={handleZoomIn}
          >
            <FiZoomIn />
          </Button>
        </HStack>
      </HStack>
      {renderContent()}
    </Box>
  );
};

const LiquidityChartLoader = () => (
  <HStack p={8} justifyContent="space-between" alignItems="flex-end">
    <Skeleton
      height="120px"
      width="40px"
      startColor="neutral.700"
      endColor="neutral.800"
    />
    <Skeleton
      height="130px"
      width="40px"
      startColor="neutral.700"
      endColor="neutral.800"
    />
    <Skeleton
      height="160px"
      width="40px"
      startColor="neutral.700"
      endColor="neutral.800"
    />
    <Skeleton
      height="130px"
      width="40px"
      startColor="neutral.700"
      endColor="neutral.800"
    />
    <Skeleton
      height="120px"
      width="40px"
      startColor="neutral.700"
      endColor="neutral.800"
    />
    <Skeleton
      height="160px"
      width="40px"
      startColor="neutral.700"
      endColor="neutral.800"
    />
    <Skeleton
      height="200px"
      width="40px"
      startColor="neutral.700"
      endColor="neutral.800"
    />
    <Skeleton
      height="140px"
      width="40px"
      startColor="neutral.700"
      endColor="neutral.800"
    />
    <Skeleton
      height="130px"
      width="40px"
      startColor="neutral.700"
      endColor="neutral.800"
    />
    <Skeleton
      height="120px"
      width="40px"
      startColor="neutral.700"
      endColor="neutral.800"
    />
    <Skeleton
      height="140px"
      width="40px"
      startColor="neutral.700"
      endColor="neutral.800"
    />
    <Skeleton
      height="120px"
      width="40px"
      startColor="neutral.700"
      endColor="neutral.800"
    />
    <Skeleton
      height="190px"
      width="40px"
      startColor="neutral.700"
      endColor="neutral.800"
    />
  </HStack>
);

export default LiquidityChart;
